import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  CurecallButton,
  CurecallCheckbox,
  CurecallDatePicker,
  CurecallInputLabel,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'src/utils/tw-merge.util'

const formatDisplayDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-')
  return `${day}/${month}/${year}`
}

export default function SchedulingOptions() {
  const { t } = useTranslation()
  const { setValue, watch } = useFormContext()

  const weekdays = [
    { label: t('pages.configuration.messages.weekday-mon'), value: 1 },
    { label: t('pages.configuration.messages.weekday-tue'), value: 2 },
    { label: t('pages.configuration.messages.weekday-wed'), value: 3 },
    { label: t('pages.configuration.messages.weekday-thu'), value: 4 },
    { label: t('pages.configuration.messages.weekday-fri'), value: 5 },
    { label: t('pages.configuration.messages.weekday-sat'), value: 6 },
    { label: t('pages.configuration.messages.weekday-sun'), value: 7 },
  ]

  const avoidedWeekdays: number[] = watch('schedule.avoidedWeekdays') ?? []
  const avoidPublicHolidays: boolean = watch('schedule.avoidPublicHolidays') ?? false
  const avoidedDates: string[] = watch('schedule.avoidedDates') ?? []

  const [pendingDate, setPendingDate] = useState<string | null>(null)
  const [datePickerKey, setDatePickerKey] = useState(0)

  const toggleWeekday = (dayValue: number) => {
    const updated = avoidedWeekdays.includes(dayValue)
      ? avoidedWeekdays.filter((d) => d !== dayValue)
      : [...avoidedWeekdays, dayValue]
    setValue('schedule.avoidedWeekdays', updated)
  }

  const handleAddDate = () => {
    if (!pendingDate || avoidedDates.includes(pendingDate)) return
    setValue('schedule.avoidedDates', [...avoidedDates, pendingDate].sort())
    setPendingDate(null)
    setDatePickerKey((k) => k + 1)
  }

  const handleRemoveDate = (date: string) => {
    setValue(
      'schedule.avoidedDates',
      avoidedDates.filter((d) => d !== date),
    )
  }

  return (
    <div className="c-flex c-flex-col c-border-t c-border-grey-400 c-py-2">
      <h3 className="c-my-2 c-p-0 c-font-nexa c-text-xs c-font-bold c-uppercase c-tracking-wider c-text-grey-600 c-pb-2">
        {t('pages.configuration.messages.exclude-dates-section-title')}
      </h3>

      <div className="c-flex c-flex-col c-gap-5">
        <div>
          <CurecallInputLabel
            label={t('pages.configuration.messages.avoid-weekdays-label')}
            tooltipContent={t('pages.configuration.messages.avoid-weekdays-tooltip')}
            tooltipPosition="right"
          />
          <div className="c-flex c-flex-wrap c-gap-2 c-mt-2">
            {weekdays.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => toggleWeekday(value)}
                className={twMerge(
                  'c-rounded-full c-border c-px-4 c-py-1.5 c-font-nexa c-text-sm c-transition-colors',
                  avoidedWeekdays.includes(value)
                    ? 'c-border-indigo-600 c-bg-indigo-600 c-text-white'
                    : 'c-border-grey-400 c-bg-white c-text-grey-900 hover:c-border-indigo-400',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <CurecallCheckbox
          isByDefaultChecked={avoidPublicHolidays}
          onChange={(e) => setValue('schedule.avoidPublicHolidays', e.target.checked)}
          label={t('pages.configuration.messages.avoid-public-holidays-label')}
          tooltipContent={t('pages.configuration.messages.avoid-public-holidays-description')}
          tooltipPosition="right"
        />

        <div>
          <div className="c-flex c-items-end c-gap-4">
            <CurecallDatePicker
              key={datePickerKey}
              onChangeDate={(date) => setPendingDate(date)}
              localeLanguage={navigator.language}
              label={t('pages.configuration.messages.avoid-dates-label')}
              tooltipContent={t('pages.configuration.messages.avoid-dates-tooltip')}
              tooltipPosition="right"
            />
            <CurecallButton
              appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
              onClick={handleAddDate}
              isDisabled={!pendingDate}
              className="c-mb-1.5"
            >
              {t('pages.configuration.messages.avoid-dates-add')}
            </CurecallButton>
          </div>

          {avoidedDates.length > 0 && (
            <div className="c-mt-3 c-flex c-flex-wrap c-gap-2">
              {avoidedDates.map((date) => (
                <span
                  key={date}
                  className="c-inline-flex c-items-center c-gap-1.5 c-rounded-full c-border c-border-pink-300 c-bg-pink-100 c-px-3 c-py-1 c-font-nexa c-text-sm c-text-pink-800"
                >
                  {formatDisplayDate(date)}
                  <button
                    type="button"
                    onClick={() => handleRemoveDate(date)}
                    aria-label={t('pages.configuration.messages.avoid-dates-remove-aria-label', { date })}
                    className="c-ml-0.5 c-rounded-full c-text-pink-400 hover:c-text-pink-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
